import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabaseClient';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const PAGE_SIZE = 24;

const decodeJwtPayload = (jwt) => {
  try {
    const part = String(jwt || '').split('.')[1] || '';
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch (_e) {
    return null;
  }
};

const parseFunctionsInvokeError = async (error) => {
  try {
    const ctx = error?.context;
    if (!ctx || typeof ctx?.clone !== 'function') {
      return {
        message: error?.message,
        status: undefined,
        statusText: undefined,
        body: undefined,
        rawText: undefined
      };

    }

    const res = ctx;
    const status = res?.status;
    const statusText = res?.statusText;

    let rawText;
    try {
      rawText = await res.clone().text();
    } catch (_e) {
      rawText = undefined;
    }

    let body;
    try {
      body = rawText ? JSON.parse(rawText) : undefined;
    } catch (_e) {
      body = undefined;
    }

    return {
      message: error?.message,
      status,
      statusText,
      body,
      rawText
    };
  } catch (_e) {
    return {
      message: error?.message,
      status: undefined,
      statusText: undefined,
      body: undefined,
      rawText: undefined
    };
  }
};

const mapClientRow = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    type: row.type,
    organizationName: row.organization_name,
    name: row.name,
    phone: row.phone,
    email: row.email,
    status: row.status,
    memo: row.memo,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
};

const AdminEditModal = ({ isOpen, onClose, admin, onSave }) => {
  const isCreateMode = !admin;
  const [formData, setFormData] = useState({
    type: 'practice',
    organizationName: '',
    name: '',
    phone: '',
    email: '',
    password: '',
    status: 'active',
    memo: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (admin) {
      setFormData({
        type: admin?.type || 'practice',
        organizationName: admin?.organizationName || '',
        name: admin?.name || '',
        phone: admin?.phone || '',
        email: admin?.email || '',
        password: '',
        status: admin?.status || 'active',
        memo: admin?.memo || ''
      });
    } else {
      setFormData({
        type: 'practice',
        organizationName: '',
        name: '',
        phone: '',
        email: '',
        password: '',
        status: 'active',
        memo: ''
      });
    }

    setErrors({});
    setIsLoading(false);
  }, [isOpen, admin]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.type) {
      newErrors.type = '유형을 선택해주세요';
    }

    if (!formData?.organizationName?.trim()) {
      newErrors.organizationName = '시설/단체명을 입력해주세요';
    }

    if (!formData?.name?.trim()) {
      newErrors.name = '담당자 이름을 입력해주세요';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = '전화번호를 입력해주세요';
    } else if (!/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = '올바른 전화번호 형식이 아닙니다';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }

    if (isCreateMode && (!formData?.password || String(formData.password).trim().length < 6)) {
      newErrors.password = '비밀번호는 최소 6자 이상이어야 합니다';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors(prev => ({ ...prev, submit: '' }));

    try {
      await onSave({ ...(admin || {}), ...formData });
      onClose();
    } catch (err) {
      setErrors(prev => ({ ...prev, submit: err?.message || '저장 중 오류가 발생했습니다' }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const typeOptions = [
    { value: 'practice', label: '연습장 운영자' },
    { value: 'club', label: '클럽 운영자' },
    { value: 'court', label: '코트 운영자' }
  ];

  const statusOptions = [
    { value: 'active', label: '활성' },
    { value: 'inactive', label: '비활성' }
  ];

  return (
    <div
      className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-250" aria-hidden="true" />
      <div className="relative z-300 w-full max-w-2xl bg-card rounded-xl shadow-2xl border border-border my-8">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                {isCreateMode ? '클라이언트 추가' : '클라이언트 수정'}
              </h2>
              <p className="text-sm caption text-muted-foreground">
                {isCreateMode ? '새로운 클라이언트 계정을 등록합니다' : '클라이언트 정보를 수정합니다'}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press disabled:opacity-50"
              aria-label="닫기"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="유형"
                options={typeOptions}
                value={formData?.type}
                onChange={(value) => handleChange('type', value)}
                error={errors?.type}
                disabled={isLoading}
              />

              <Select
                label="상태"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
                disabled={isLoading}
              />
            </div>

            <Input
              label="시설/클럽/코트 이름"
              type="text"
              placeholder="예: OO 픽클볼 연습장"
              value={formData?.organizationName}
              onChange={(e) => handleChange('organizationName', e?.target?.value)}
              error={errors?.organizationName}
              required
              disabled={isLoading}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="담당자 이름"
                type="text"
                placeholder="홍길동"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                error={errors?.name}
                required
                disabled={isLoading}
              />

              <Input
                label="전화번호"
                type="tel"
                placeholder="010-1234-5678"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                error={errors?.phone}
                required
                disabled={isLoading}
              />
            </div>

            <Input
              label="이메일"
              type="email"
              placeholder="example@email.com"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              error={errors?.email}
              disabled={isLoading || !isCreateMode}
            />

            <Input
              label={isCreateMode ? '비밀번호' : '비밀번호 재설정(선택)'}
              type="password"
              placeholder={isCreateMode ? '6자 이상' : '변경하지 않으려면 비워두세요'}
              value={formData?.password}
              onChange={(e) => handleChange('password', e?.target?.value)}
              error={errors?.password}
              disabled={isLoading}
            />

            <Input
              label="메모"
              type="text"
              placeholder="추가 정보"
              value={formData?.memo}
              onChange={(e) => handleChange('memo', e?.target?.value)}
              disabled={isLoading}
            />

            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-md p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isLoading} fullWidth>
                취소
              </Button>
              <Button type="submit" variant="warning" loading={isLoading} iconName="Save" iconPosition="right" fullWidth>
                저장
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const typeLabel = (type) => {
  switch (type) {
    case 'practice':
      return '연습장 운영자';
    case 'club':
      return '클럽 운영자';
    case 'court':
      return '코트 운영자';
    default:
      return '운영자';
  }
};

const statusLabel = (status) => {
  return status === 'inactive' ? '비활성' : '활성';
};

const SuperAdminAdminsPage = () => {
  const navigate = useNavigate();

  const [admins, setAdmins] = useState([]);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [query, typeFilter, statusFilter]);

  const loadAdmins = async (targetPage) => {
    setListLoading(true);
    setListError('');
    try {
      const normalizedPage = Math.max(1, targetPage || 1);
      const from = (normalizedPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let q = supabase
        .from('profiles')
        .select('*', { count: 'exact' });

      q = q.eq('role', 'clients');

      if (typeFilter) q = q.eq('type', typeFilter);
      if (statusFilter) q = q.eq('status', statusFilter);

      const keyword = (query || '').trim();
      if (keyword) {
        const pattern = `%${keyword}%`;
        q = q.or(`organization_name.ilike.${pattern},name.ilike.${pattern},phone.ilike.${pattern},email.ilike.${pattern}`);
      }

      const { data, error, count } = await q
        .order('updated_at', { ascending: false })
        .range(from, to);

      if (error) {
        setListError('목록을 불러오지 못했습니다');
        setAdmins([]);
        setTotalCount(0);
        setListLoading(false);
        return;
      }

      setAdmins((data || []).map(mapClientRow).filter(Boolean));
      setTotalCount(count || 0);
      setListLoading(false);
    } catch (e) {
      setListError('목록을 불러오지 못했습니다');
      setAdmins([]);
      setTotalCount(0);
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, typeFilter, statusFilter]);

  const filteredAdmins = useMemo(() => admins || [], [admins]);

  const typeOptions = [
    { value: '', label: '전체 유형' },
    { value: 'practice', label: '연습장 운영자' },
    { value: 'club', label: '클럽 운영자' },
    { value: 'court', label: '코트 운영자' }
  ];

  const statusOptions = [
    { value: '', label: '전체 상태' },
    { value: 'active', label: '활성' },
    { value: 'inactive', label: '비활성' }
  ];

  const openCreate = () => {
    setEditingAdmin(null);
    setEditModalOpen(true);
  };

  const openEdit = (admin) => {
    setEditingAdmin(admin);
    setEditModalOpen(true);
  };

  const handleSave = async (next) => {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (sessionError || !token) {
        throw new Error('세션이 만료되었습니다. 다시 로그인해주세요.');
      }

      if (!next?.id && next?.email) {
        const { data: dupRows, error: dupError } = await supabase
          .from('profiles')
          .select('id')
          .eq('role', 'clients')
          .eq('email', String(next.email).trim())
          .limit(1);

        if (dupError) {
          console.error('[SuperAdminClients] email duplication check failed', dupError);
        }

        if (!dupError && Array.isArray(dupRows) && dupRows.length > 0) {
          throw new Error('이미 등록된 이메일입니다. 다른 이메일을 사용해주세요.');
        }
      }

      try {
        const payload = decodeJwtPayload(token);
        console.groupCollapsed('[SuperAdminClients] token debug');
        console.log('supabaseUrl:', import.meta.env.VITE_SUPABASE_URL);
        console.log('iss:', payload?.iss);
        console.log('aud:', payload?.aud);
        console.log('role:', payload?.role);
        console.log('exp:', payload?.exp);
        console.groupEnd();
      } catch (_e) {
        // ignore token parse errors
      }

      const body = {
        id: next?.id,
        type: next?.type,
        organizationName: next?.organizationName,
        name: next?.name,
        phone: next?.phone,
        email: next?.email,
        status: next?.status,
        memo: next?.memo
      };

      if (next?.password) {
        body.password = next.password;
      }

      const { data, error } = await supabase.functions.invoke('client-upsert', {
        body,
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });
      if (error) {
        const parsed = await parseFunctionsInvokeError(error);
        console.group('[SuperAdminClients] client-upsert invoke error');
        console.error('message:', error?.message);
        console.error('status:', parsed?.status, parsed?.statusText);
        console.error('body:', parsed?.body);
        console.error('rawText:', parsed?.rawText);
        console.error('details:', error);
        console.groupEnd();

        const msg =
          parsed?.status === 409
            ? '이미 등록된 이메일입니다. 다른 이메일을 사용해주세요.'
            : parsed?.body?.error || parsed?.body?.message || error?.message || '저장에 실패했습니다.';
        throw new Error(msg);
      }

      if (!data?.ok) {
        console.group('[SuperAdminClients] client-upsert failed');
        console.error('data:', data);
        console.groupEnd();
        throw new Error(data?.error || '저장 중 오류가 발생했습니다');
      }

      await loadAdmins(page);
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = (admin) => {
    const run = async () => {
      const ok = window.confirm('정말 삭제하시겠습니까?');
      if (!ok) return;

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (sessionError || !token) {
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        return;
      }

      const { data, error } = await supabase.functions.invoke('client-delete', {
        body: { id: admin?.id },
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });
      if (error || !data?.ok) {
        const parsed = error ? await parseFunctionsInvokeError(error) : null;
        console.group('[SuperAdminClients] client-delete error');
        console.error('invokeError:', error);
        if (parsed) {
          console.error('status:', parsed?.status, parsed?.statusText);
          console.error('body:', parsed?.body);
          console.error('rawText:', parsed?.rawText);
        }
        console.error('data:', data);
        console.groupEnd();
        alert(data?.error || parsed?.body?.error || error?.message || '삭제 중 오류가 발생했습니다');
        return;
      }

      const remainingAfterDelete = Math.max(0, (totalCount || 0) - 1);
      const nextTotalPages = Math.max(1, Math.ceil(remainingAfterDelete / PAGE_SIZE));
      const nextPage = Math.min(page, nextTotalPages);
      setPage(nextPage);
    };

    run();
  };

  const handleLogout = () => {
    const run = async () => {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        // ignore
      }
      navigate('/');
    };

    run();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-warning/10 rounded-md flex items-center justify-center">
              <Icon name="Shield" size={22} color="var(--color-warning)" />
            </div>
            <div>
              <p className="text-sm font-heading font-semibold text-foreground">슈퍼관리자</p>
              <p className="text-xs caption text-muted-foreground">클라이언트 계정 관리</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="Home" iconPosition="left" onClick={() => navigate('/')}
            >
              홈
            </Button>
            <Button variant="ghost" size="sm" iconName="LogOut" iconPosition="left" onClick={handleLogout}
            >
              로그아웃
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">클라이언트 계정</h1>
            <p className="text-sm caption text-muted-foreground mt-2">
              연습장/클럽/코트 운영자 계정을 등록하고 관리합니다
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="warning" iconName="UserPlus" iconPosition="left" onClick={openCreate}
            >
              클라이언트 추가
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              label="검색"
              type="text"
              placeholder="이름/전화/이메일/시설명"
              value={query}
              onChange={(e) => setQuery(e?.target?.value)}
            />

            <Select
              label="유형"
              options={typeOptions}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v)}
              clearable
            />

            <Select
              label="상태"
              options={statusOptions}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              clearable
            />

            <div className="flex items-end">
              <Button
                variant="outline"
                fullWidth
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => {
                  setQuery('');
                  setTypeFilter('');
                  setStatusFilter('');
                }}
              >
                초기화
              </Button>
            </div>
          </div>
        </div>

        {listLoading ? (
          <div className="bg-muted border border-border rounded-xl p-6">
            <p className="text-sm text-muted-foreground">로딩 중...</p>
          </div>
        ) : filteredAdmins?.length === 0 ? (
          <div className="bg-muted border border-border rounded-xl p-6">
            <p className="text-sm text-foreground font-medium">등록된 클라이언트가 없습니다.</p>
            <p className="text-xs caption text-muted-foreground mt-2">"클라이언트 추가"로 클라이언트 계정을 등록해 주세요.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredAdmins.map((a) => (
              <div key={a?.id} className="bg-card border border-border rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs caption text-muted-foreground">{typeLabel(a?.type)}</p>
                    <p className="text-lg font-heading font-semibold text-foreground mt-1 truncate">{a?.organizationName || '-'}</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      담당자: <span className="text-foreground font-medium">{a?.name || '-'}</span>
                    </p>
                  </div>

                  <div className={
                    a?.status === 'inactive'
                      ? 'px-2 py-1 rounded-full bg-muted text-xs font-semibold text-muted-foreground border border-border'
                      : 'px-2 py-1 rounded-full bg-success/10 text-xs font-semibold text-success border border-success/20'
                  }>
                    {statusLabel(a?.status)}
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Phone" size={16} className="text-muted-foreground" />
                    <span className="text-foreground font-medium">{a?.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground truncate">{a?.email || '-'}</span>
                  </div>

                  {a?.memo && (
                    <div className="bg-muted/50 border border-border rounded-lg p-3">
                      <p className="text-xs caption text-muted-foreground">메모</p>
                      <p className="text-sm text-foreground mt-1">{a?.memo}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-5">
                  <Button variant="outline" size="sm" iconName="Edit" iconPosition="left" onClick={() => openEdit(a)} fullWidth>
                    수정
                  </Button>
                  <Button variant="danger" size="sm" iconName="Trash2" iconPosition="left" onClick={() => handleDelete(a)} fullWidth>
                    삭제
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {listError && (
          <div className="mt-6 bg-error/10 border border-error/20 rounded-xl p-4 flex items-start gap-2">
            <Icon name="AlertCircle" size={18} className="text-error mt-0.5 flex-shrink-0" />
            <p className="text-xs caption text-error">{listError}</p>
          </div>
        )}

        <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-xs caption text-muted-foreground">
            총 {totalCount}개
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={listLoading || page <= 1}
            >
              이전
            </Button>
            <div className="text-xs caption text-muted-foreground px-2">
              {page} / {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={listLoading || page >= totalPages}
            >
              다음
            </Button>
          </div>
        </div>

        <AdminEditModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          admin={editingAdmin}
          onSave={handleSave}
        />
      </main>
    </div>
  );
};

export default SuperAdminAdminsPage;
